// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { createFragmentContainer, RelayProp } from "react-relay";
// event-summary-time
import { eventSummaryTimeFragments } from "./EventSummaryTime.gql";
import { EventSummaryRow as s } from "./EventSummary.styles";
// util
import { ZonedMoment } from "@util/time-zone.util";
import { getValue } from "@util/errorable.util";
import {
  getAbsoluteDateTime,
  getAbsoluteDate,
} from "@clockwise/client-commons/src/util/format-time";
import { EFlex } from "@clockwise/client-commons/src/util/event-tag";
// material-ui
import { AccessTime, Assistant } from "@material-ui/icons";

interface IContainer {
  org: ISchema.IOrg;
  event: ISchema.IEvent;
  startTime: ZonedMoment;
  endTime: ZonedMoment;
  unemphasized?: boolean;
  isAllDay?: boolean;
  selectedFlex?: EFlex;
  calendarId?: string;
}

interface IProps extends IContainer {
  relay: RelayProp;
}
const EventSummaryTimeCmpt = ({
  event,
  startTime,
  endTime,
  unemphasized,
  isAllDay,
  selectedFlex,
}: IProps) => {
  const text = unemphasized ? s.textLessEmphasized : s.textEmphasized;
  const list = getValue(event.eventFlexibilityErrorable)?.list || [];
  const flexStringMap = list.reduce<Record<string, string>>((acc, { flex, flexString }) => {
    acc[flex] = flexString;
    return acc;
  }, {});

  if (selectedFlex) {
    return (
      <div style={s.container}>
        <Assistant style={{ ...s.icon, color: s.green }} htmlColor={s.green} />
        <div style={{ ...text, color: s.green }}>{flexStringMap[selectedFlex] ?? ""}</div>
      </div>
    );
  }

  /**
   * All day events have an end time of midnight on the next day, so we back it up to the previous day.
   * i.e. An all day event for Nov 1st will have an end time of midnight on Nov 2nd.
   */
  const endTimeAdjusted = endTime.clone().subtract(1, "minutes");
  const isSingleDay = startTime.isSame(endTimeAdjusted, "days");

  const formattedDate = isAllDay
    ? getAbsoluteDate(startTime, isSingleDay ? undefined : endTimeAdjusted, true)
    : getAbsoluteDateTime(startTime, endTime);

  return (
    <div style={{ ...s.container, alignItems: "center" }}>
      <AccessTime style={s.icon} htmlColor={s.icon.color} />
      <div style={text}>{formattedDate}</div>
    </div>
  );
};

export const EventSummaryTime = createFragmentContainer<IContainer>(
  EventSummaryTimeCmpt,
  eventSummaryTimeFragments,
);
